import { Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons';
import AddressAutoComplete from '@components/Form/AddressAutoComplete';
import { Address } from '@customTypes/user';

interface AddressSectionProps {
  isEditing: boolean;
  address?: Address | null;
  streetFocused: boolean;
  onStreetFocus: () => void;
  onStreetBlur: () => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAddressSelect: (street: string, city?: string, region?: string, postal?: string) => void;
}

export function AddressDetails({ address: addr }: { address?: Address }) {
  if (!addr || (!addr.street_address && !addr.city && !addr.region && !addr.postal_code && !addr.country)) {
    return <div>—</div>;
  }

  const cityRegion = [addr.city, addr.region].filter(Boolean).join(', ');
  const cityRegionZip = [cityRegion, addr.postal_code].filter(Boolean).join(' ');

  return (
    <>
      {addr.street_address && <div>{addr.street_address}</div>}
      {addr.street_address_2 && <div>{addr.street_address_2}</div>}
      {cityRegionZip && <div>{cityRegionZip}</div>}
      {addr.country && <div>{addr.country}</div>}
    </>
  );
}

export default function AddressSection({
  isEditing,
  address,
  streetFocused,
  onStreetFocus,
  onStreetBlur,
  onChange,
  onAddressSelect,
}: AddressSectionProps) {
  if (!isEditing) {
    return (
      <div className='detail-item'>
        <span className='label'>Address</span>
        <div className='value'>
          <AddressDetails address={address || undefined} />
        </div>
      </div>
    );
  }

  const editAddress = address || ({} as Address);

  return (
    <div className='mt-4 mb-3'>
      <div className='d-flex align-items-center mb-2'>
        <FontAwesomeIcon icon={faLocationDot} className='me-2 text-secondary' />
        <h6 className='mb-0 fw-bold'>Address</h6>
      </div>

      <Form.Group className='mb-2 position-relative'>
        <Form.Label>Street</Form.Label>
        <Form.Control
          type='text'
          name='address.street_address'
          value={editAddress.street_address || ''}
          onChange={onChange}
          onFocus={onStreetFocus}
          onBlur={onStreetBlur}
          autoComplete='off'
        />
        <AddressAutoComplete
          value={editAddress.street_address || ''}
          focused={streetFocused}
          onChange={onAddressSelect}
        />
      </Form.Group>
      <Form.Group className='mb-2'>
        <Form.Label>Suite/Apt</Form.Label>
        <Form.Control
          type='text'
          name='address.street_address_2'
          value={editAddress.street_address_2 || ''}
          onChange={onChange}
        />
      </Form.Group>
      <div className='row'>
        <Form.Group className='col-md-6 mb-2'>
          <Form.Label>City</Form.Label>
          <Form.Control type='text' name='address.city' value={editAddress.city || ''} onChange={onChange} />
        </Form.Group>
        <Form.Group className='col-md-6 mb-2'>
          <Form.Label>State</Form.Label>
          <Form.Control type='text' name='address.region' value={editAddress.region || ''} onChange={onChange} />
        </Form.Group>
      </div>
      <div className='row'>
        <Form.Group className='col-md-6 mb-2'>
          <Form.Label>Zip</Form.Label>
          <Form.Control
            type='text'
            name='address.postal_code'
            value={editAddress.postal_code || ''}
            onChange={onChange}
          />
        </Form.Group>
        <Form.Group className='col-md-6 mb-2'>
          <Form.Label>Country</Form.Label>
          <Form.Control type='text' name='address.country' value={editAddress.country || ''} onChange={onChange} />
        </Form.Group>
      </div>
    </div>
  );
}
